import React, { useEffect, useState, useRef } from "react";
import LoginBackgroundNew from "../../assets/images/LoginBackgroundNew.png";
import HeaderLogoNew from "../../assets/images/HeaderLogoNew.svg";
import Logo from "../../assets/images/Logo.svg";
import {
  Backdrop,
  Button,
  LogoLoader,
  NoData,
  showToast,
} from "../../components";
import { useNavigate } from "react-router-dom";
import axiosRequest from "../../redux/slices/universal/axiosService";
import { ENDPOINTS } from "../../redux/apiConfig";
import { useSearchParams } from "react-router-dom";

const EOnboardingLogin = ({ pinLength = 6 }) => {
  // const dispatch = useDispatch();
  // const auth = useSelector((state) => state.auth);
  const [pin, setPin] = useState(Array(pinLength).fill(""));
  const [searchParams] = useSearchParams();
  const querytoken = searchParams.get("token");
  const [isloading, setIsLoading] = useState(true);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const inputRefs = useRef([]);
  const loading = false;
  const navigate = useNavigate();

  useEffect(() => {
    // Focus the first input box on firstly
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
    verifyTempAccess();
  }, []);

  useEffect(() => {
    setPin(Array(pinLength).fill(""));
  }, [pinLength]);

  const handleChange = (index, value) => {
    // Only allow single digit numbers (0-9)
    const numericValue = value.replace(/[^0-9]/g, "");
    if (value && !numericValue) return;

    const newPin = [...pin];
    newPin[index] = numericValue.slice(-1);
    setPin(newPin);

    // Auto-focus next input if value entered
    if (numericValue && index < pinLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle Enter key to submit
    if (e.key === "Enter" && isComplete) {
      handleLogin();
      return;
    }

    // Handle backspace click
    if (e.key === "Backspace") {
      if (!pin[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else {
        const newPin = [...pin];
        newPin[index] = "";
        setPin(newPin);
      }
    }

    // Handle left arrow click
    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Handle right arrow click
    if (e.key === "ArrowRight" && index < pinLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").trim();

    if (!/^\d+$/.test(pastedData)) return;

    const digits = pastedData.slice(0, pinLength).split("");
    const newPin = [...pin];

    digits.forEach((digit, index) => {
      if (index < pinLength) {
        newPin[index] = digit;
      }
    });

    setPin(newPin);

    const nextEmptyIndex = newPin.findIndex((val) => val === "");
    const focusIndex = nextEmptyIndex === -1 ? pinLength - 1 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleFocus = (index) => {
    inputRefs.current[index]?.select();
  };

  const isComplete = pin.every((digit) => digit !== "");
  const pinValue = pin.join("");

  const verifyTempAccess = async () => {
    try {
      if (!querytoken) {
        setIsLoading(false);
        setIsUnauthorized(true);
        throw new Error("Missing token");
      }
      const res = await axiosRequest({
        method: "GET",
        url: ENDPOINTS.WF_AUTHENTICATION.VERIFY_TEMP_TOKEN,
        params: {
          token: querytoken,
        },
      });

      if (res.success) {
        setIsLoading(false);
      }
      if (res.errorCode === 401) {
        setIsLoading(false);
        setIsUnauthorized(true);
      }
    } catch (e) {
      console.error(e);
      showToast("Something went wrong", "error");
    }
  };

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      if (!isComplete) {
        showToast(
          `Please enter complete ${pinLength}-digit PIN`,
          "error",
          5000,
        );
        return;
      } else {
        if (!querytoken) {
          throw new Error("Missing token");
        }
        const res = await axiosRequest({
          method: "GET",
          url: ENDPOINTS.WF_AUTHENTICATION.VERIFY_TEMP_LOGIN,
          params: {
            token: querytoken,
            pincode: pinValue,
          },
        });

        const onboardingInfo = res?.data?.data || {};
        console.log({ onboardingInfo });

        if (res.success) {
          setPin(Array(pinLength).fill(""));
          navigate(`/onboarding-documents/fill`, { state: { onboardingInfo } });
        }
        if (res.errorCode === 401) {
          setIsUnauthorized(true);
        }
      }
    } catch (e) {
      console.error(e);
      showToast("Something went wrong", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // useEffect(() => {
  //   if (auth.error) {
  //     showToast(auth.error, "error", 5000);
  //     dispatch(clearError());
  //   }
  // }, [auth.error, dispatch]);
  if (isUnauthorized) {
    return (
      <div className="flex justify-center items-center h-screen">
        <NoData
          title="Unauthorized or Broken Link!"
          desc="Please Contact Support"
        />
        ;
      </div>
    );
  }

  return (
    <>
      {isloading ? (
        <Backdrop isOpen={isloading}>
          <LogoLoader />
        </Backdrop>
      ) : (
        <div
          className="w-full min-h-screen bg-no-repeat bg-cover bg-center bg-fixed"
          style={{ backgroundImage: `url(${LoginBackgroundNew})` }}
        >
          <div className="xs:p-5 md:p-10 gap-10 min-h-screen flex flex-col flex-1">
            <div>
              <div className="select-none">
                <img src={HeaderLogoNew} alt="Kult Works" className="" />
              </div>
            </div>
            <div className="flex-1 flex items-center justify-between gap-10 xs:flex-col lg:flex-row text-white">
              <div className="xs:hidden lg:flex flex-col gap-10">
                <h1 className="text-shadow font-semibold mb-3 xs:text-5xl sm:text-6xl md:text-[86px]">
                  Welcome...
                </h1>
                <p className="xs:text-base md:text-[16px] leading-[24px] w-full max-w-[650px] font-light">
                  Supercharge Your Recruitment with Next-Gen Technology. Unlock
                  the power of AI-driven, all-in-one ATS designed to help
                  staffing agencies recruit smarter, faster, and more
                  efficiently.
                </p>
                <div className="xs:mt-4 lg:mt-4">
                  <Button title={"Learn More"} size="xl" />
                </div>
              </div>
              <div className="flex-1 flex justify-end items-center">
                <div className="w-full xs:min-w-[280px] sm:min-w-[420px] max-w-[560px]">
                  <div className={"rounded-3xl w-full gradient-bg "}>
                    <div className="w-full flex-1 flex flex-col items-center justify-between xs:px-4 sm:px-10 pt-16 pb-20">
                      <img
                        src={Logo}
                        alt="Kult Works Logo"
                        className="w-[60px] lg:w-[80px] h-[60px] lg:h-[80px]"
                      />
                      <div>
                        <h2 className="text-center text-white xs:text-3xl text-4xl font-semibold my-10">
                          Sign in
                        </h2>
                      </div>
                      <div className="w-full max-w-80 flex flex-col gap-8">
                        <div className="flex flex-col gap-4">
                          <label className="text-white text-sm font-medium text-center">
                            Enter {pinLength}-Digit PIN
                          </label>
                          <div className="flex justify-center gap-2">
                            {pin.map((digit, index) => (
                              <input
                                key={index}
                                ref={(el) => (inputRefs.current[index] = el)}
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={1}
                                value={digit}
                                onChange={(e) =>
                                  handleChange(index, e.target.value)
                                }
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                onPaste={index === 0 ? handlePaste : undefined}
                                onFocus={() => handleFocus(index)}
                                className="w-12 h-14 text-center text-2xl font-semibold border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 bg-white text-gray-800 cursor-pointer"
                                aria-label={`PIN digit ${index + 1}`}
                              />
                            ))}
                          </div>
                        </div>

                        <Button
                          title={loading ? "Loading..." : "Login"}
                          disabled={loading || !isComplete}
                          onClick={handleLogin}
                        />

                        {!isComplete && pin.some((d) => d !== "") && (
                          <p className="text-center text-sm text-gray-300 -mt-4">
                            Please enter all {pinLength} digits
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EOnboardingLogin;
